/**
 * RoleRoute Component
 *
 * A route guard that enforces role-based access control.
 * Must be used inside a ProtectedRoute to ensure authentication.
 *
 * Features:
 * - Supports single role or array of roles
 * - Strict role matching (no hierarchy) — user's role must be in allowed list
 * - Redirects unauthorized users to their role-specific dashboard
 * - Shows loading state while checking
 */

import { type FC } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useHasRole, useAuthInitialized, useAuthLoading, useUserRole } from '@/hooks/useAuth';
import { PageLoadingSpinner } from '@/components/common/LoadingSpinner';
import { useLanguage } from '@/hooks/useLanguage';
import type { UserRole } from '@/types/api';

interface RoleRouteProps {
  /** Required role(s) to access the route */
  roles: UserRole | UserRole[];
  /** Path to redirect if role check fails (defaults to /) */
  fallbackPath?: string;
  /** Optional fallback component while loading */
  fallback?: React.ReactNode;
}

/**
 * Get the default dashboard path for a role
 */
export function getRoleDashboard(role: UserRole | undefined): string {
  switch (role) {
    case 'ADMIN':
      return '/admin';
    case 'TEACHER':
      return '/teacher';
    case 'STUDENT':
      return '/student';
    default:
      return '/';
  }
}

/**
 * RoleRoute guards routes that require specific role(s).
 *
 * @example
 * // Single role
 * <Route element={<RoleRoute roles="ADMIN" />}>
 *   <Route path="admin/*" element={<AdminRoutes />} />
 * </Route>
 *
 * @example
 * // Multiple roles
 * <Route element={<RoleRoute roles={['ADMIN', 'TEACHER']} />}>
 *   <Route path="gradebook" element={<Gradebook />} />
 * </Route>
 *
 * @example
 * // With custom fallback path
 * <Route element={<RoleRoute roles="ADMIN" fallbackPath="/dashboard" />}>
 *   <Route path="settings" element={<AdminSettings />} />
 * </Route>
 */
export const RoleRoute: FC<RoleRouteProps> = ({
  roles,
  fallbackPath,
  fallback,
}) => {
  const location = useLocation();
  const { t } = useLanguage();
  const isInitialized = useAuthInitialized();
  const isLoading = useAuthLoading();
  const userRole = useUserRole();

  // Normalize roles to array
  const allowedRoles = Array.isArray(roles) ? roles : [roles];
  const hasRequiredRole = useHasRole(allowedRoles);

  // Determine fallback path - use role-specific dashboard if not specified
  const redirectPath = fallbackPath ?? getRoleDashboard(userRole);

  // Show loading state while auth is initializing
  if (!isInitialized || isLoading) {
    return fallback ?? (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <PageLoadingSpinner
          variant="primary"
          label={t('common.loading')}
        />
      </div>
    );
  }

  // Redirect if user doesn't have required role
  if (!hasRequiredRole) {
    return (
      <Navigate
        to={redirectPath}
        state={{ from: location, reason: 'insufficient_role' }}
        replace
      />
    );
  }

  // User has required role - render child routes
  return <Outlet />;
};

export default RoleRoute;
